import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, FileSpreadsheet, Receipt, FileBarChart2, CalendarSync, Plug,
  BedDouble, Briefcase, Building2, BarChart3, CreditCard,
  Settings as SettingsIcon, HelpCircle, QrCode, LogOut, ChevronRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useProperty } from "@/context/PropertyContext";
import LanguageSelector from "@/components/LanguageSelector";
import FoxLogo from "@/components/FoxLogo";

/**
 * Full-screen drawer that opens from the bottom tab bar's Menu icon.
 * Shows the property switcher, all the sections that don't fit in the tab
 * bar, language selector and logout. Slides up from the bottom.
 */
export default function MenuSheet({ open, onClose }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { properties, current, select } = useProperty();
  const nav = useNavigate();

  const handleLogout = async () => {
    onClose();
    try { await logout(); } catch (_e) { /* clear local anyway */ }
    nav("/login", { replace: true });
  };

  const groups = [
    {
      title: t("nav.compliance"),
      items: [
        { to: "/app/alloggiati",     Icon: FileSpreadsheet, label: t("nav.police"),         testid: "nav-alloggiati" },
        { to: "/app/tourist-tax",    Icon: Receipt,         label: t("nav.tourist_tax"),    testid: "nav-tourist-tax" },
        { to: "/app/istat",          Icon: FileBarChart2,   label: t("nav.istat"),          testid: "nav-istat" },
        { to: "/app/ical-sync",      Icon: CalendarSync,    label: t("nav.ota_sync"),       testid: "nav-ical-sync" },
        { to: "/app/channel-manager",Icon: Plug,            label: t("nav.channel_manager"),testid: "nav-channel-manager" },
      ],
    },
    {
      title: t("nav.management"),
      items: [
        { to: "/app/self-checkin", Icon: QrCode,      label: t("nav.self_checkin"), testid: "nav-self-checkin" },
        { to: "/app/rooms",        Icon: BedDouble,   label: t("nav.rooms"),        testid: "nav-rooms" },
        { to: "/app/companies",    Icon: Briefcase,   label: t("nav.companies"),    testid: "nav-companies" },
        { to: "/app/properties",   Icon: Building2,   label: t("nav.properties"),   testid: "nav-properties" },
        { to: "/app/reports",      Icon: BarChart3,   label: t("nav.reports"),      testid: "nav-reports" },
        { to: "/app/billing",      Icon: CreditCard,  label: t("nav.billing"),      testid: "nav-billing" },
        { to: "/app/settings",     Icon: SettingsIcon,label: t("nav.settings"),     testid: "nav-settings" },
        { to: "/app/help",         Icon: HelpCircle,  label: t("nav.help"),         testid: "nav-help" },
        { to: "/app/qr-poster",    Icon: QrCode,      label: t("nav.qr_poster"),    testid: "nav-qr-poster" },
      ],
    },
  ];

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[80] bg-slate-950/50 backdrop-blur-sm"
            onClick={onClose}
            data-testid="menu-sheet-backdrop"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 260, damping: 30 }}
            className="fixed bottom-0 inset-x-0 z-[90] bg-white rounded-t-3xl shadow-2xl max-h-[85vh] overflow-y-auto"
            data-testid="menu-sheet"
          >
            {/* Handle bar */}
            <div className="pt-3 flex justify-center"><div className="w-10 h-1 rounded-full bg-slate-300" /></div>
            <div className="px-6 pt-4 pb-8">
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <FoxLogo size={28} />
                  <span className="font-heading font-semibold text-xl">Xenia<span className="text-blue-600">.</span></span>
                </div>
                <button onClick={onClose} data-testid="menu-sheet-close"
                  className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors">
                  <X className="w-4 h-4 text-slate-600" />
                </button>
              </div>

              {/* Property switcher */}
              {properties.length > 0 && (
                <div className="mb-6 border border-slate-200 rounded-2xl p-4 bg-slate-50">
                  <p className="text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2">{t("nav.property")}</p>
                  <div className="space-y-1">
                    {properties.map((p) => (
                      <button key={p.id} onClick={() => { select(p.id); onClose(); }}
                        data-testid={`sheet-property-${p.id}`}
                        className={`w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors ${
                          p.id === current?.id
                            ? "bg-blue-50 text-blue-700 font-medium"
                            : "hover:bg-white text-slate-700"
                        }`}>
                        <span className="truncate">{p.name}</span>
                        {p.id === current?.id && <span className="text-[10px] uppercase tracking-wide">•attiva</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Nav groups */}
              {groups.map((group) => (
                <div key={group.title} className="mb-6">
                  <p className="text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2 px-2">{group.title}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {group.items.map(({ to, Icon, label, testid }) => (
                      <NavLink key={to} to={to} data-testid={testid} onClick={onClose}
                        className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-xl border transition-all ${
                          isActive
                            ? "border-blue-200 bg-blue-50 text-blue-700"
                            : "border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700"
                        }`}>
                        <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} />
                        <span className="text-sm font-medium truncate flex-1">{label}</span>
                        <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}

              {/* Footer */}
              <div className="pt-5 border-t border-slate-200 flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-500 mb-1">{t("nav.signed_in_as", "Connesso come")}</div>
                  <div className="text-sm font-medium text-slate-900 truncate max-w-[180px]">{user?.email}</div>
                </div>
                <div className="flex items-center gap-3">
                  <LanguageSelector />
                  <button onClick={handleLogout} data-testid="menu-sheet-logout"
                    className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-red-600 transition-colors">
                    <LogOut className="w-4 h-4" strokeWidth={1.75} />
                    {t("nav.logout")}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
